// Facebook Login Detection Service
// Detects if user is logged into Facebook and retrieves their Facebook ID

interface FacebookLoginStatus {
  isLoggedIn: boolean;
  facebookUserId?: string;
  error?: string;
}

export class FacebookLoginDetectionService {
  private static instance: FacebookLoginDetectionService;

  public static getInstance(): FacebookLoginDetectionService {
    if (!FacebookLoginDetectionService.instance) {
      FacebookLoginDetectionService.instance = new FacebookLoginDetectionService();
    }
    return FacebookLoginDetectionService.instance;
  }

  /**
   * Check if user is logged into Facebook and get their Facebook ID
   * This uses the Facebook SDK to check login status
   */
  async getFacebookLoginStatus(): Promise<FacebookLoginStatus> {
    return new Promise((resolve) => {
      // Check if Facebook SDK is loaded
      if (typeof (window as any).FB === 'undefined') {
        console.log('Facebook SDK not loaded, cannot detect login status');
        resolve({ isLoggedIn: false, error: 'Facebook SDK not loaded' });
        return;
      }

      try {
        // Check Facebook login status
        (window as any).FB.getLoginStatus((response: any) => {
          console.log('Facebook login status response:', response);

          if (response.status === 'connected') {
            // User is logged into Facebook and has authorized the app
            const facebookUserId = response.authResponse?.userID;
            console.log('✅ User is logged into Facebook, ID:', facebookUserId);

            resolve({
              isLoggedIn: true,
              facebookUserId: facebookUserId
            });
          } else {
            // User is not logged into Facebook or hasn't authorized the app
            console.log('❌ User not logged into Facebook or not authorized');
            resolve({ isLoggedIn: false });
          }
        });
      } catch (error) {
        console.error('Error checking Facebook login status:', error);
        resolve({
          isLoggedIn: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  }

  /**
   * Initialize Facebook SDK if not already loaded
   * This is optional - the pixel should already load the SDK
   */
  initializeFacebookSDK(): Promise<boolean> {
    return new Promise((resolve) => {
      // Check if SDK is already loaded
      if (typeof (window as any).FB !== 'undefined') {
        console.log('Facebook SDK already loaded');
        resolve(true);
        return;
      }

      // Check if pixel has loaded FB object
      if (typeof (window as any).fbq !== 'undefined') {
        // Pixel is loaded, but full SDK might not be
        console.log('Facebook Pixel loaded, but full SDK may not be available');

        // Wait a bit and check again
        setTimeout(() => {
          resolve(typeof (window as any).FB !== 'undefined');
        }, 1000);
        return;
      }

      console.log('Facebook SDK/Pixel not detected');
      resolve(false);
    });
  }

  /**
   * Get Facebook login ID with timeout and error handling
   * This is the main method to use in checkout flow
   */
  async getFacebookLoginId(timeoutMs: number = 2000): Promise<string | null> {
    try {
      // First ensure SDK is available
      const sdkAvailable = await this.initializeFacebookSDK();
      if (!sdkAvailable) {
        console.log('Facebook SDK not available, skipping Facebook login detection');
        return null;
      }

      // Create a timeout promise
      const timeoutPromise = new Promise<FacebookLoginStatus>((resolve) => {
        setTimeout(() => {
          resolve({ isLoggedIn: false, error: 'Timeout' });
        }, timeoutMs);
      });

      // Race between login status check and timeout
      const result = await Promise.race([
        this.getFacebookLoginStatus(),
        timeoutPromise
      ]);

      if (result.isLoggedIn && result.facebookUserId) {
        console.log('✅ Successfully detected Facebook login ID:', result.facebookUserId);
        return result.facebookUserId;
      }

      console.log('No Facebook login detected or timeout occurred');
      return null;

    } catch (error) {
      console.error('Error getting Facebook login ID:', error);
      return null;
    }
  }

  /**
   * Enhanced detection that tries multiple methods
   * Fallback method if primary detection fails
   */
  async getAdvancedFacebookLoginId(): Promise<string | null> {
    try {
      // Method 1: Standard SDK check
      const standardResult = await this.getFacebookLoginId(1500);
      if (standardResult) {
        return standardResult;
      }

      // Method 2: Check if user came from Facebook (referrer-based)
      if (document.referrer.includes('facebook.com') || document.referrer.includes('fb.com')) {
        console.log('User came from Facebook, but login ID not detected via SDK');
      }

      // Method 3: Check for Facebook-specific cookies (advanced)
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

      // Look for Facebook user cookies (these are typically encrypted/hashed)
      const facebookCookies = Object.keys(cookies).filter(key =>
        key.startsWith('c_user') || key.startsWith('xs') || key.includes('facebook')
      );

      if (facebookCookies.length > 0) {
        console.log('Facebook-related cookies detected, but cannot extract user ID safely');
      }

      return null;

    } catch (error) {
      console.error('Error in advanced Facebook login detection:', error);
      return null;
    }
  }
}

// Export singleton instance
export const facebookLoginService = FacebookLoginDetectionService.getInstance();